/**
 * Color utility functions for handling hex colors, alpha compositing, and color conversions
 */

/**
 * Warning: Consider using addAlphaToHex instead for new code. If you actually need transparency then this method can be used.
 * @param hexCode { string } with hash (#) or without hash
 * @param alpha { number } alpha value for the color, can be a number between 0 and 1 or a number between 0 and 100
 * @returns rgba color if the hex code is valid, otherwise an empty string
 */
export function hexToRGBA(hexCode: string, alpha = 1): string {
	if (!hexCode) {
		return '';
	}
	let hex = hexCode.replace('#', '');
	if (hex.length === 3) {
		hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
	}
	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	if (alpha > 1 && alpha <= 100) {
		alpha = alpha / 100;
	}
	return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Converts a hex color with alpha transparency to a solid hex color by compositing over white background.
 *
 * This function takes a hex color and alpha value, then calculates what the solid color would look like
 * when the transparent color is placed over a white background. This is useful for getting the visual
 * appearance of a transparent color without actually using transparency.
 *
 * @param hexColor - The hex color string with or without the '#' prefix (e.g., "#FF0000" or "FF0000")
 * @param alpha - The alpha value as a decimal between 0 and 1, or as a percentage between 0 and 100
 *                - Values between 0-1 are treated as decimal (0.5 = 50% opacity)
 *                - Values between 1-100 are treated as percentage (50 = 50% opacity)
 * @param backgroundColor - The background color to composite over (defaults to white "#FFFFFF")
 *
 * @returns A solid 6-digit hex color string that visually matches the transparent color over the background
 *
 * @example
 * // Using decimal alpha values (0-1)
 * addAlphaToHex("#FF0000", 0.5)     // Returns "#FF8080" (red composited over white at 50% opacity)
 * addAlphaToHex("FF0000", 1)        // Returns "#FF0000" (fully opaque red, no change)
 * addAlphaToHex("#00FF00", 0)       // Returns "#FFFFFF" (fully transparent green over white = white)
 *
 * @example
 * // Using percentage alpha values (1-100)
 * addAlphaToHex("#0000FF", 50)      // Returns "#8080FF" (blue composited over white at 50% opacity)
 * addAlphaToHex("FF00FF", 75)       // Returns "#FF40FF" (magenta composited over white at 75% opacity)
 *
 * @example
 * // Edge cases
 * addAlphaToHex("#FFFFFF", 0.1)     // Returns "#FFFFFF" (white over white = white)
 * addAlphaToHex("#000000", 99)      // Returns "#030303" (black composited over white at 99% opacity)
 */
export function addAlphaToHex(hexColor: string, alpha: number, backgroundColor = '#FFFFFF'): string {
	// Validate inputs
	if (!hexColor) return '';

	// Ensure alpha is between 0 and 1 (if provided as a decimal percentage)
	if (alpha > 1) alpha = alpha / 100;

	// Helper function to convert hex to RGB (following existing pattern from hexToRGBA)
	const hexToRgb = (hexCode: string): { r: number; g: number; b: number } | null => {
		let hex = hexCode.replace('#', '');
		if (hex.length === 3) {
			hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
		}

		// Validate hex format
		if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
			return null;
		}

		return {
			r: parseInt(hex.slice(0, 2), 16),
			g: parseInt(hex.slice(2, 4), 16),
			b: parseInt(hex.slice(4, 6), 16),
		};
	};

	// Convert both colors to RGB
	const colorRgb = hexToRgb(hexColor);
	const bgRgb = hexToRgb(backgroundColor);

	// Handle invalid hex inputs
	if (!colorRgb || !bgRgb) {
		return '';
	}

	// Alpha compositing formula: result = alpha * foreground + (1 - alpha) * background
	const resultR = Math.round(alpha * colorRgb.r + (1 - alpha) * bgRgb.r);
	const resultG = Math.round(alpha * colorRgb.g + (1 - alpha) * bgRgb.g);
	const resultB = Math.round(alpha * colorRgb.b + (1 - alpha) * bgRgb.b);

	// Convert back to hex
	const toHex = (value: number): string => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0');

	return `#${toHex(resultR)}${toHex(resultG)}${toHex(resultB)}`.toUpperCase();
}
